import React, { useState, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaCheck } from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import useConfirm from '../../hooks/useConfirm';
import { useLanguage } from '../../context/LanguageContext';
import './PlanCard.scss';

export const SilverStar = () => {
  const silverStarGradientId = useId();
  const silverStarStrokeId = useId();
  const silverInnerId = useId();
  const silverHighlightId = useId();

  return (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 4l3.5 7.1 7.8 1.1-5.7 5.5 1.4 7.8-7-3.7-7 3.7 1.4-7.8-5.7-5.5 7.8-1.1z" fill={`url(#${silverStarGradientId})`} stroke={`url(#${silverStarStrokeId})`} strokeWidth="1" strokeLinejoin="round" />
    <circle cx="16" cy="16" r="4" fill={`url(#${silverInnerId})`} opacity="0.4"/>
    <path d="M16 8l2 4 4.5 0.6-3.3 3.2 0.8 4.5-4-2.1-4 2.1 0.8-4.5-3.3-3.2 4.5-0.6z" fill={`url(#${silverHighlightId})`} opacity="0.5"/>
    <defs>
      <linearGradient id={silverStarGradientId} x1="16" y1="4" x2="16" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#E8E8E8"/>
        <stop offset="50%" stopColor="#C0C0C0"/>
        <stop offset="100%" stopColor="#A8A8A8"/>
      </linearGradient>
      <linearGradient id={silverStarStrokeId} x1="16" y1="4" x2="16" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#B0B0B0"/>
        <stop offset="100%" stopColor="#808080"/>
      </linearGradient>
      <radialGradient id={silverInnerId} cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor="#FFFFFF"/>
        <stop offset="100%" stopColor="#C0C0C0"/>
      </radialGradient>
      <linearGradient id={silverHighlightId} x1="16" y1="8" x2="16" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFFFFF"/>
        <stop offset="100%" stopColor="#E0E0E0"/>
      </linearGradient>
    </defs>
  </svg>
  );
};

export const GoldStar = () => {
  const goldStarGradientId = useId();
  const goldStarStrokeId = useId();
  const goldInnerId = useId();
  const goldHighlightId = useId();

  return (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 4l3.5 7.1 7.8 1.1-5.7 5.5 1.4 7.8-7-3.7-7 3.7 1.4-7.8-5.7-5.5 7.8-1.1z" fill={`url(#${goldStarGradientId})`} stroke={`url(#${goldStarStrokeId})`} strokeWidth="1" strokeLinejoin="round" />
    <circle cx="16" cy="16" r="4" fill={`url(#${goldInnerId})`} opacity="0.5"/>
    <path d="M16 8l2 4 4.5 0.6-3.3 3.2 0.8 4.5-4-2.1-4 2.1 0.8-4.5-3.3-3.2 4.5-0.6z" fill={`url(#${goldHighlightId})`} opacity="0.6"/>
    <circle cx="13" cy="13" r="1.5" fill="#FFF9E6" opacity="0.8"/>
    <ellipse cx="20" cy="18" rx="1.2" ry="0.8" fill="#FFF4D0" opacity="0.6"/>
    <defs>
      <linearGradient id={goldStarGradientId} x1="16" y1="4" x2="16" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFD700"/>
        <stop offset="30%" stopColor="#FFA500"/>
        <stop offset="70%" stopColor="#DAA520"/>
        <stop offset="100%" stopColor="#B8860B"/>
      </linearGradient>
      <linearGradient id={goldStarStrokeId} x1="16" y1="4" x2="16" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#DAA520"/>
        <stop offset="100%" stopColor="#8B6914"/>
      </linearGradient>
      <radialGradient id={goldInnerId} cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor="#FFFFFF"/>
        <stop offset="100%" stopColor="#FFD700"/>
      </radialGradient>
      <linearGradient id={goldHighlightId} x1="16" y1="8" x2="16" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFFFFF"/>
        <stop offset="100%" stopColor="#FFE87C"/>
      </linearGradient>
    </defs>
  </svg>
  );
};

// Module-level cache: one network request shared by all mounted PlanCards,
// refreshed at most every 5 minutes.
let cachedPlanSettingsResponse = null;
let cachedPlanSettingsAt = 0;
let inflightPlanSettingsPromise = null;
const PLAN_SETTINGS_TTL_MS = 5 * 60 * 1000;

function getCachedPlanSettings() {
  if (
    cachedPlanSettingsResponse &&
    Date.now() - cachedPlanSettingsAt < PLAN_SETTINGS_TTL_MS
  ) {
    return Promise.resolve(cachedPlanSettingsResponse);
  }
  if (!inflightPlanSettingsPromise) {
    inflightPlanSettingsPromise = apiHelper
      .get('/plans/settings')
      .then((response) => {
        cachedPlanSettingsResponse = response;
        cachedPlanSettingsAt = Date.now();
        return response;
      })
      .finally(() => {
        inflightPlanSettingsPromise = null;
      });
  }
  return inflightPlanSettingsPromise;
}

const PlanCard = ({ tier = 'Silver', price, period, benefits = [], onContact, onActivate, highlighted = false, isCurrentPlan = false }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useLanguage();
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.settings || {});
  
  const rawUserPlan = (profile && (profile.plan || profile.subscription?.tier)) || user?.plan || 'none';
  const currentUserPlan = String(rawUserPlan || 'none').toLowerCase();
  const isPlanExpired =
    profile?.subscription?.isExpired === true ||
    user?.subscription?.isExpired === true;
  const treatAsCurrentPlan = isCurrentPlan && !isPlanExpired;
  
  const tierKey = String(tier || 'silver').toLowerCase();

  // State for admin-configured plan settings
  const [planSettings, setPlanSettings] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Fetch admin-configured plan settings — shared module-level cache so every
  // PlanCard instance on the page (Dashboard renders two) makes ONE request.
  useEffect(() => {
    let cancelled = false;
    const fetchPlanSettings = async () => {
      try {
        const response = await getCachedPlanSettings();
        if (!cancelled && response && response.plans) {
          const planData = response.plans.find(p => p.planId === tierKey);
          if (planData) {
            setPlanSettings(planData);
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch plan settings:', err);
      } finally {
        if (!cancelled) setLoadingSettings(false);
      }
    };

    fetchPlanSettings();
    return () => { cancelled = true; };
  }, [tierKey]);

  const defaults = {
    silver: {
      price: '₹ 0',
      period: 'Per Month',
      benefits: [
        '',
        '',
        '',
        '',
        ''
      ]
    },
    gold: {
      price: '₹ 0',
      period: 'Per Month',
      benefits: [
        '',
        '',
        '',
        '',
        ''
      ]
    }
  };

  const meta = defaults[tierKey] || {};
  
  // Use admin settings if available, otherwise fall back to props or defaults
  const formatPrice = (amount) => `₹ ${amount.toLocaleString('en-IN')}`;
  
  let finalPrice, finalOriginalPrice, discountPercentage, offerLabel, isOfferActive;
  let effectivePriceValue = 0;
  let originalPriceValue = 0;
  
  if (planSettings) {
    effectivePriceValue = Number(planSettings.price) || 0;
    originalPriceValue = Number(planSettings.basePrice30 ?? planSettings.price) || effectivePriceValue;
    finalPrice = formatPrice(effectivePriceValue);
    finalOriginalPrice = originalPriceValue !== effectivePriceValue ? formatPrice(originalPriceValue) : null;
    discountPercentage = planSettings.discountPercentage || 0;
    offerLabel = planSettings.offerLabel || '';
    isOfferActive = planSettings.isOfferActive || false;
  } else {
    finalPrice = price || meta.price || '';
    finalOriginalPrice = null;
    discountPercentage = 0;
    offerLabel = '';
    isOfferActive = false;
  }
  
  const finalPeriod = period || (planSettings?.billingCycle === 'yearly' ? t('planCard.perYear') : t('planCard.perMonth')) || meta.period || '';
  const finalBenefits = Array.isArray(planSettings?.features)
    ? planSettings.features.filter((feature) => typeof feature === 'string' && feature.trim().length > 0)
    : [];
  const hasDiscount = isOfferActive && discountPercentage > 0;
  const shouldShowOfferBadge = isOfferActive && (Boolean(offerLabel) || discountPercentage > 0);
  const savingsAmount = hasDiscount ? Math.max(0, originalPriceValue - effectivePriceValue) : 0;

  const defaultContact = () => {
    try {
      window.dispatchEvent(new CustomEvent('closePlan'));
      window.dispatchEvent(new CustomEvent('openHelp'));
    } catch (e) {
      try { navigate(`/plans?plan=${tierKey}`); } catch (err) {}
    }
  };

  const defaultActivate = async () => {
    if (treatAsCurrentPlan) {
      if (tierKey === 'silver') {
        // Handle "Upgrade to Gold" case
        const confirmed = await confirm(t('planCard.confirmUpgrade'), {
          title: t('planCard.dialogUpgradeTitle'),
          confirmText: t('planCard.upgrade'),
          cancelText: t('common.cancel')
        });
        if (!confirmed) {
          return;
        }
        try {
          window.dispatchEvent(new CustomEvent('closePlan'));
        } catch (e) {}
        navigate('/payment?plan=gold');
        return;
      }

      if (tierKey === 'gold') {
        // Handle "Downgrade to Silver" case
        const confirmed = await confirm(t('planCard.confirmDowngrade'), {
          title: t('planCard.dialogDowngradeTitle'),
          confirmText: t('planCard.downgrade'),
          cancelText: t('common.cancel')
        });
        if (!confirmed) {
          return;
        }
        try {
          window.dispatchEvent(new CustomEvent('closePlan'));
        } catch (e) {}
        navigate('/payment?plan=silver');
        return;
      }
      
      // Already has the plan (fallback)
      try {
        window.dispatchEvent(new CustomEvent('closePlan'));
        navigate('/dashboard');
      } catch (e) {
        navigate('/dashboard');
      }
      return;
    }

    // Non-current plan interactions

    // 1. Alert if user tries to activate the same plan (e.g. if isCurrentPlan was false but tier as user plan)
    if (tierKey === currentUserPlan && !isPlanExpired) {
      await confirm(t('planCard.alreadyActive').replace('{plan}', t(`planCard.${tierKey}`)), {
        title: t('planCard.dialogActivePlanTitle'),
        confirmText: t('planCard.ok'),
        cancelText: ''
      });
      return;
    }

    // 2. Alert if changing plan
    if (currentUserPlan !== 'none' && tierKey !== currentUserPlan) {
      const confirmChange = await confirm(t('planCard.confirmChange').replace('{from}', t(`planCard.${currentUserPlan}`)).replace('{to}', t(`planCard.${tierKey}`)), {
        title: t('planCard.dialogChangePlanTitle'),
        confirmText: t('planCard.confirm'),
        cancelText: t('common.cancel')
      });
      if (!confirmChange) return;
    }

    console.log('[PlanCard] Activate clicked, tierKey:', tierKey);
    try {
      window.dispatchEvent(new CustomEvent('closePlan'));
    } catch (e) {
      console.error('[PlanCard] Error dispatching closePlan:', e);
    }
    
    const url = `/payment?plan=${tierKey}`;
    console.log('[PlanCard] Navigating to:', url);
    navigate(url);
  };

  const handleContact = onContact || defaultContact;
  const handleActivate = onActivate || defaultActivate;

  const isGold = tierKey.includes('gold');
  const isSilver = tierKey.includes('silver');

  return (
    <div className={`plan-card ${isGold ? 'plan-card--gold' : isSilver ? 'plan-card--silver' : ''} ${highlighted ? 'plan-card--highlight':''}`}>
      {/* Discount Badge */}
      {shouldShowOfferBadge && (
        <div className="plan-card__discount-badge">
          {offerLabel && discountPercentage 
            ? <><span>{offerLabel}</span><img src="/percentage (1).png" alt="off" className="plan-card__badge-separator" /><span>{discountPercentage}% OFF</span></>
            : offerLabel || (discountPercentage && savingsAmount > 0 
              ? `${discountPercentage}% OFF • Save ₹${savingsAmount.toLocaleString('en-IN')}` 
              : `${discountPercentage}% OFF`)}
        </div>
      )}
      
      <div className="plan-card__header">
        <div className="plan-card__badge">{isGold ? <GoldStar /> : isSilver ? <SilverStar /> : null}</div>
        <div className="plan-card__meta">
          <h4 className='plan'>{t(`planCard.${tierKey}`)}</h4>
          <div className="plan-card__note">{isGold ? t('planCard.unlimitedFeatures') : isSilver ? t('planCard.limitedFeatures') : ''}</div>
        </div>
        <div className="plan-card__price-block">
          {hasDiscount && savingsAmount > 0 && (
            <div className="plan-card__savings-row">
              <div className="plan-card__savings">{t('planCard.save')} ₹ {savingsAmount.toLocaleString('en-IN')}</div>
            </div>
          )}
          <div className="plan-card__price-line">
            {hasDiscount && finalOriginalPrice && (
              <div className="plan-card__original-price">{finalOriginalPrice}</div>
            )}
            <div className={`plan-card__price-amount ${hasDiscount ? 'plan-card__price-amount--discounted' : ''}`}>
              {finalPrice}
            </div>
          </div>
          <div className="plan-card__price-period">{finalPeriod}</div>
        </div>
      </div>

      <ul className="plan-card__list">
        {finalBenefits.map((b, i) => (
          <li key={i}><FaCheck className="plan-modal__benefit-icon" />{b}</li>
        ))}
      </ul>

      <div className="plan-card__actions">
        <button className="btn btn--outline" onClick={handleContact} type="button">{t('planCard.contactUs')}</button>
        <button className="btn btn--primary" onClick={handleActivate} type="button">
          {treatAsCurrentPlan 
            ? (tierKey === 'silver' ? t('planCard.upgrade') : (tierKey === 'gold' ? t('planCard.downgrade') : t('planCard.managePlan'))) 
            : `${t('planCard.activate')} ${t(`planCard.${tierKey}`)}`}
        </button>
      </div>
    </div>
  );
};

export default PlanCard;
